import React from "react";

/**
 * R3 Phase 6A.5 — small reusable cost progress meter.
 *
 *   <AICostMeter currentUsd={0.0234} maxUsd={5} testid="ai-cost-meter" />
 *
 *   < 70 % cap : rose-200 fill (calm "you have headroom")
 *   70-90 %    : amber-400 fill (heads up)
 *   ≥ 90 %     : rose-600 fill (about to hit the cap)
 *
 * Numbers above the bar use a fixed 4-decimal format so the meter
 * doesn't reflow as cents accrue.  Both `currentUsd` and `maxUsd` are
 * required — the bar is a hard cap visualisation, not a probability.
 */
export default function AICostMeter({
  currentUsd,
  maxUsd,
  testid = "ai-cost-meter",
  label = "LLM spend",
  className = "",
}) {
  const cur = Math.max(0, Number(currentUsd) || 0);
  const cap = Math.max(0.0001, Number(maxUsd) || 0);
  const pct = Math.min(100, (cur / cap) * 100);
  let fillClass = "bg-rose-300";
  let zone = "ok";
  if (pct >= 90) {
    fillClass = "bg-rose-600";
    zone = "danger";
  } else if (pct >= 70) {
    fillClass = "bg-amber-400";
    zone = "warning";
  }
  return (
    <div className={`space-y-1 ${className}`} data-testid={testid} data-zone={zone}>
      <div className="flex items-baseline justify-between text-[11px] text-slate-500">
        <span className="font-medium text-slate-600">{label}</span>
        <span className="font-mono text-slate-700" data-testid={`${testid}-readout`}>
          ${cur.toFixed(4)}
          <span className="px-1 text-slate-400">/</span>
          ${cap.toFixed(2)}
          <span className="ml-1.5 text-[10px] text-slate-400">
            ({pct.toFixed(0)}%)
          </span>
        </span>
      </div>
      <div
        className="relative h-2 w-full overflow-hidden rounded-full bg-slate-100"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label} ${pct.toFixed(0)} percent of cap`}
      >
        <div
          className={`h-full ${fillClass} transition-[width] duration-300 ease-out`}
          style={{ width: `${pct}%` }}
          data-testid={`${testid}-fill`}
        />
      </div>
    </div>
  );
}
